import axios, { AxiosInstance, AxiosError } from "axios";
import { PlatformUiEnvToHostMap } from "../data-provider/platform-ui-env-to-host-map";
import { usePlatformUiEnv } from "../env/use-platform-ui-env";
import { AccessTokenContext } from "../auth/access-token-context";
import { useContext, useMemo } from "react";
import { PlatformUiEnv } from "../platform-ui-envs.type";
import { UserContext } from "../auth/user-context";
import { AuActiveOrgHeader, OrgDto } from "@arrowsup/platform-dtos";

export const makeApi = (
  env: PlatformUiEnv,
  getAccessToken: () => Promise<string>,
  activeOrg?: OrgDto
): AxiosInstance => {
  const headers: Record<string, string | number> = {};

  // The active org is validated against the user's memberships server-side.
  if (activeOrg) {
    headers[AuActiveOrgHeader] = activeOrg.id;
  }

  const thing = axios.create({
    baseURL: PlatformUiEnvToHostMap[env],
    headers,
  });

  thing.interceptors.request.use(async (config) => {
    config.headers.set("Authorization", `Bearer ${await getAccessToken()}`);

    return config;
  });
  thing.interceptors.response.use(
    (resp) => {
      return resp;
    },
    (error: AxiosError) => {
      alert(error.response?.data ?? "Request failed");

      return Promise.reject(error);
    }
  );

  return thing;
};

/**
 * Create an Axios instance to communicate with the backend.
 * This goes entirely around the DataProvider, and lets us hit any backend
 * endpoint.
 *
 * @returns the axios instance
 */
export const useApi: () => AxiosInstance = () => {
  const env = usePlatformUiEnv();
  const getAccessToken = useContext(AccessTokenContext).getAccessToken;
  const userContext = useContext(UserContext);

  return useMemo(() => {
    return makeApi(env, getAccessToken, userContext.activeOrg);
  }, [env, getAccessToken, userContext.activeOrg]);
};
